<template>
  <div class="flex flex-col items-center">
    <BaseCard class="relative w-full sm:w-auto sm:min-w-96">
      <BaseInnerLoading v-if="isSubmitting" />
      <SetChangeResetPasswordForm
        :require-current-password="
          action === 'changePassword'
        "
        @submit="onSubmit"
      />
    </BaseCard>
  </div>
</template>

<script setup lang="ts">
import type {
  SetChangeResetPasswordFormInput,
  SetChangeResetPasswordFormOutput,
} from "@/composables/forms/useSetChangeResetPasswordForm"
import type { SubmissionContext } from "vee-validate"

const { currentUser } = useCurrentUser()
const { setPageTitle } = usePageTitle()
const { navigateToHome } = useNavigateToHome()

const action = computed<"setPassword" | "changePassword">(
  () =>
    currentUser.value?.hasPassword
      ? "changePassword"
      : "setPassword"
)

watchEffect(() =>
  setPageTitle(
    action.value === "changePassword"
      ? "Change Password"
      : "Set Password"
  )
)

const isSubmitting = ref(false)

const onSubmit = async (
  params: SetChangeResetPasswordFormOutput,
  context: SubmissionContext<SetChangeResetPasswordFormInput>
) => {
  isSubmitting.value = true
  try {
    await selfChangePassword({
      currentPassword: params.currentPassword,
      password: params.password,
    })
    toast.success(
      action.value === "setPassword"
        ? "Password set successfully"
        : "Password changed successfully"
    )
    navigateToHome()
  } catch (error) {
    if (!(error instanceof ApiError)) {
      toast.error("Failed to change password")
      return
    }

    switch (error.code) {
      case "invalid-current-password":
        context.setFieldError(
          "currentPassword",
          "Current password is incorrect"
        )
        break
      case "current-password-required":
        context.setFieldError(
          "currentPassword",
          "Current password is required"
        )
        break
      case "invalid-password":
      case "same-as-current-password":
        context.setFieldError(
          "password",
          error.detail ||
            "Password does not meet requirements"
        )
        break
      default:
        toast.error("Failed to change password")
    }
  } finally {
    isSubmitting.value = false
  }
}
</script>
